import React, { useState } from 'react';
import LoadingState from './LoadingState';
import ThinkingState from './ThinkingState';
import StreamingText from './StreamingText';
import ApprovalCard from './ApprovalCard';
import ToolChips from './ToolChips';
import TaskRows from './TaskRows';
import ChatComposer from './ChatComposer';
import PromptBar from './PromptBar';
import RecommendationCard from './RecommendationCard';
import ContextCards from './ContextCards';
import { DiffTable, RecordsTable } from './DiffTable';
import CodeBlock from './CodeBlock';

/* ─────────────────────────────────────────────────────────
 * PRIMITIVES GALLERY & LIVE PLAYGROUND
 * Interactive showcase demonstrating every crafted primitive.
 * ───────────────────────────────────────────────────────── */

export default function PrimitivesGallery({
  onSelectPrompt,
}: {
  onSelectPrompt?: (prompt: string) => void;
}) {
  const [loadingVariant, setLoadingVariant] = useState('Drive');
  const [thinkingVariant, setThinkingVariant] = useState('Steps');
  const [taskVariant, setTaskVariant] = useState('Capsules');
  const [promptBarVariant, setPromptBarVariant] = useState('Rounded');

  return (
    <div className="flex-1 overflow-y-auto p-6 max-w-5xl mx-auto w-full flex flex-col gap-10 pb-36">
      {/* Header */}
      <div className="border-b border-line pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-ink flex items-center gap-2">
            <span className="size-6 rounded-lg bg-gradient-to-tr from-accent to-green flex items-center justify-center text-white text-xs">
              ✦
            </span>
            Crafted Primitives Gallery
          </h1>
          <p className="text-xs text-ink-3 mt-0.5">
            Interactive, copy-paste ready components for AI-native interfaces from Beautiful UI
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[11px] px-2 py-1 rounded bg-field text-ink-2 border border-line">
            12 AI Primitives Live
          </span>
        </div>
      </div>

      {/* Grid of Primitives */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 1. Loading State */}
        <div className="flex flex-col gap-3 p-4 rounded-card bg-surface shadow-card border border-line">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-ink">1. Loading State</h3>
              <p className="text-xs text-ink-3">Pixel-grid loader with wavefront shimmer and live timer</p>
            </div>
            <div className="flex gap-1">
              {['Drive', 'Dots', 'Orbit'].map((v) => (
                <button
                  key={v}
                  onClick={() => setLoadingVariant(v)}
                  className={`px-2 py-0.5 rounded-[5px] text-[11px] font-medium transition-colors ${
                    loadingVariant === v ? 'bg-ink text-canvas font-semibold' : 'bg-field text-ink-3 hover:text-ink'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div className="p-4 rounded-[10px] bg-field/60 flex items-center justify-center min-h-[70px]">
            <LoadingState label="Churning batch data" variant={loadingVariant} />
          </div>
        </div>

        {/* 2. Thinking State */}
        <div className="flex flex-col gap-3 p-4 rounded-card bg-surface shadow-card border border-line">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-ink">2. Thinking State</h3>
              <p className="text-xs text-ink-3">Expandable traces: steps, reasoning, search, coding</p>
            </div>
            <div className="flex gap-1">
              {['Steps', 'Reasoning', 'Search', 'Coding'].map((v) => (
                <button
                  key={v}
                  onClick={() => setThinkingVariant(v)}
                  className={`px-2 py-0.5 rounded-[5px] text-[11px] font-medium transition-colors ${
                    thinkingVariant === v ? 'bg-ink text-canvas font-semibold' : 'bg-field text-ink-3 hover:text-ink'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div className="p-2 rounded-[10px] bg-field/40">
            <ThinkingState variant={thinkingVariant} />
          </div>
        </div>

        {/* 3. Streaming Text */}
        <div className="flex flex-col gap-3 p-4 rounded-card bg-surface shadow-card border border-line">
          <div>
            <h3 className="text-sm font-semibold text-ink">3. Streaming Text</h3>
            <p className="text-xs text-ink-3">Streamed text with blur resolve, inline citations, & follow-ups</p>
          </div>
          <div className="p-2 rounded-[10px] bg-field/40">
            <StreamingText onFollowupSelect={(f) => onSelectPrompt && onSelectPrompt(f)} />
          </div>
        </div>

        {/* 4. Approval Card */}
        <div className="flex flex-col gap-3 p-4 rounded-card bg-surface shadow-card border border-line">
          <div>
            <h3 className="text-sm font-semibold text-ink">4. Approval Card (Human-in-the-Loop)</h3>
            <p className="text-xs text-ink-3">Interactive question wizard with auto-advance and confirmation</p>
          </div>
          <div className="flex justify-center p-2 rounded-[10px] bg-field/40">
            <ApprovalCard />
          </div>
        </div>

        {/* 5. Tool Chips */}
        <div className="flex flex-col gap-3 p-4 rounded-card bg-surface shadow-card border border-line">
          <div>
            <h3 className="text-sm font-semibold text-ink">5. Tool Chips & File Diffs</h3>
            <p className="text-xs text-ink-3">Compact execution rows with expandable what-it-did code diffs</p>
          </div>
          <div className="p-2 rounded-[10px] bg-field/40">
            <ToolChips />
          </div>
        </div>

        {/* 6. Task Rows Pipeline */}
        <div className="flex flex-col gap-3 p-4 rounded-card bg-surface shadow-card border border-line">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-ink">6. Task Rows Pipeline</h3>
              <p className="text-xs text-ink-3">Live task progress with SVG spinner rings & retry state</p>
            </div>
            <div className="flex gap-1">
              {['Capsules', 'List'].map((v) => (
                <button
                  key={v}
                  onClick={() => setTaskVariant(v)}
                  className={`px-2 py-0.5 rounded-[5px] text-[11px] font-medium transition-colors ${
                    taskVariant === v ? 'bg-ink text-canvas font-semibold' : 'bg-field text-ink-3 hover:text-ink'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div className="p-2 rounded-[10px] bg-field/40">
            <TaskRows variant={taskVariant} />
          </div>
        </div>

        {/* 7. Recommendation Card */}
        <div className="flex flex-col gap-3 p-4 rounded-card bg-surface shadow-card border border-line">
          <div>
            <h3 className="text-sm font-semibold text-ink">7. Recommendation Card</h3>
            <p className="text-xs text-ink-3">Agent suggestions with confidence gauge meters and actions</p>
          </div>
          <div className="flex justify-center p-2 rounded-[10px] bg-field/40">
            <RecommendationCard />
          </div>
        </div>

        {/* 8. Context Cards */}
        <div className="flex flex-col gap-3 p-4 rounded-card bg-surface shadow-card border border-line">
          <div>
            <h3 className="text-sm font-semibold text-ink">8. Context Cards</h3>
            <p className="text-xs text-ink-3">Retrieved knowledge chunks with similarity match scores</p>
          </div>
          <div className="flex justify-center p-2 rounded-[10px] bg-field/40">
            <ContextCards />
          </div>
        </div>

        {/* 9. Diff Sweep Table */}
        <div className="flex flex-col gap-3 p-4 rounded-card bg-surface shadow-card border border-line">
          <div>
            <h3 className="text-sm font-semibold text-ink">9. Diff Table</h3>
            <p className="text-xs text-ink-3">AI-proposed tabular sweeps with additions and deletions</p>
          </div>
          <div className="flex justify-center p-2 rounded-[10px] bg-field/40">
            <DiffTable />
          </div>
        </div>

        {/* 10. Records Filter Table */}
        <div className="flex flex-col gap-3 p-4 rounded-card bg-surface shadow-card border border-line">
          <div>
            <h3 className="text-sm font-semibold text-ink">10. Records Table</h3>
            <p className="text-xs text-ink-3">Filterable CRM-style directory with growth tier badges</p>
          </div>
          <div className="flex justify-center p-2 rounded-[10px] bg-field/40">
            <RecordsTable />
          </div>
        </div>

        {/* 11. Code Block with Sandbox */}
        <div className="flex flex-col gap-3 p-4 rounded-card bg-surface shadow-card border border-line">
          <div>
            <h3 className="text-sm font-semibold text-ink">11. Code Block & Runner</h3>
            <p className="text-xs text-ink-3">Syntax highlighting, line numbers, and live V8 runner</p>
          </div>
          <div className="flex justify-center p-2 rounded-[10px] bg-field/40">
            <CodeBlock />
          </div>
        </div>

        {/* 12. Chat Composer Panel */}
        <div className="flex flex-col gap-3 p-4 rounded-card bg-surface shadow-card border border-line">
          <div>
            <h3 className="text-sm font-semibold text-ink">12. Tabbed Chat Panel</h3>
            <p className="text-xs text-ink-3">Tabbed conversation panel with timed section streams</p>
          </div>
          <div className="flex justify-center p-2 rounded-[10px] bg-field/40">
            <ChatComposer />
          </div>
        </div>
      </div>

      {/* Full Width Prompt Bar Showcase */}
      <div className="flex flex-col gap-3 p-5 rounded-card bg-surface shadow-card border border-line">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-ink">13. Dynamic Prompt Bar Composer</h3>
            <p className="text-xs text-ink-3">
              Type @ for sources, / for commands, click model for Glimm rainbow sweep
            </p>
          </div>
          <div className="flex gap-1">
            {['Rounded', 'Pill'].map((v) => (
              <button
                key={v}
                onClick={() => setPromptBarVariant(v)}
                className={`px-2 py-0.5 rounded-[5px] text-[11px] font-medium transition-colors ${
                  promptBarVariant === v ? 'bg-ink text-canvas font-semibold' : 'bg-field text-ink-3 hover:text-ink'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
        <div className="p-4 rounded-[10px] bg-field/40 flex justify-center">
          <PromptBar
            variant={promptBarVariant}
            onSend={(p) => onSelectPrompt && onSelectPrompt(p)}
          />
        </div>
      </div>
    </div>
  );
}
